import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { AuthService } from '../../api/services/auth.service';
import { AnalyticsService, DrinkPopularityReport } from '../../api/services/analytics.service';
import { HeaderNavComponent } from 'src/app/ui/header-nav/header-nav.component';
import { FooterComponent } from 'src/app/ui/footer/footer.component';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-bar-discovery',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatCardModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatSelectModule,
    MatChipsModule, MatProgressSpinnerModule, MatSnackBarModule, MatDialogModule,
    HeaderNavComponent, FooterComponent
  ],
  templateUrl: './bar-discovery.component.html'
})
export class BarDiscoveryComponent implements OnInit {
  isLoading = false;
  isLocating = false;
  bars: any[] = [];
  filteredBars: any[] = [];

  // Filters
  searchName = '';
  searchCity = '';
  searchCountry = '';
  sortBy = 'name';
  /** Never more than MAX_RADIUS_KM; the server clamps it as well. */
  radiusKm = 100;
  /** Bars further away than this are not shown at all. */
  readonly MAX_RADIUS_KM = 100;
  /** Where the customer is, once they let us ask. */
  private coords: { lat: number; lng: number } | null = null;
  /** True when we asked for location and did not get it. */
  locationUnavailable = false;

  // Selected bar detail
  selectedBar: any = null;
  popularDrinks: DrinkPopularityReport[] = [];
  loadingDrinks = false;

  // Rating
  showRatingDialog = false;
  ratingBarId = '';
  ratingValue = 5;
  ratingStars = [1, 2, 3, 4, 5];

  constructor(
    private authService: AuthService,
    private analyticsService: AnalyticsService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Distance first. Bars are a place you walk into, so a list that spans
    // continents is noise — the whole estate came back before, 344 of them.
    // Location is asked for once; if it is refused the filters below are the
    // way to narrow by place instead.
    this.locateThenLoad();
  }

  /**
   * Ask the browser where we are, then load. Falls back to filter-only
   * searching rather than showing every bar in the world, because "within
   * 100km" is the rule and an unknown location cannot satisfy it.
   */
  private locateThenLoad(): void {
    if (!navigator.geolocation) {
      this.locationUnavailable = true;
      this.isLoading = false;
      return;
    }
    this.isLocating = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.coords = { lat: position.coords.latitude, lng: position.coords.longitude };
        this.locationUnavailable = false;
        this.isLocating = false;
        this.loadBars();
      },
      () => {
        this.isLocating = false;
        this.locationUnavailable = true;
        // Say what is actually required. It used to offer "or search by city
        // or country", which no longer shows anything: bars further than
        // 100km are never listed, and without a location nothing can be shown
        // to be inside that.
        this.snackBar.open(
          'Allow location access to see bars near you.',
          'Close', { duration: 6000 });
      },
      { timeout: 10000, maximumAge: 300000 }
    );
  }

  /**
   * Load the list, distance first.
   *
   * With a location we ask for bars within the radius (never more than
   * MAX_RADIUS_KM) and narrow THAT set by the text filters — the filters
   * refine what is near you rather than reaching past it. Without a location
   * the only honest way to bound the list by place is the city/country
   * filter, so nothing is shown until one is given.
   */
  loadBars(): void {
    if (this.coords) {
      this.isLoading = true;
      const radius = Math.min(this.radiusKm || this.MAX_RADIUS_KM, this.MAX_RADIUS_KM);
      this.authService.getNearbyBars(this.coords.lat, this.coords.lng, radius).subscribe({
        next: (bars) => {
          this.bars = this.applyTextFilters(bars);
          this.applyClientSort(this.bars);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.snackBar.open('Failed to load bars near you', 'Close', { duration: 3000 });
        }
      });
      return;
    }

    // No location, no bars. A bar more than 100km away must NEVER be shown,
    // and without knowing where the customer is there is no way to establish
    // that any bar is inside the limit — so searching by city or country here
    // would be a way around the rule, not an alternative to it. The filters
    // narrow what is already near; they never reach past it.
    this.bars = [];
    this.filteredBars = [];
    this.isLoading = false;
  }

  /**
   * The text filters, applied here because the nearby endpoint takes none.
   * Safe to do in the browser: the set is already bounded to MAX_RADIUS_KM by
   * the server, so this narrows a short list rather than standing in for a
   * query that should have been filtered in the database.
   */
  private applyTextFilters(bars: any[]): any[] {
    const has = (hay: string | undefined, needle: string) =>
      (hay || '').toLowerCase().includes(needle.toLowerCase());
    return (bars || []).filter(b =>
      (!this.searchName || has(b.name, this.searchName)) &&
      (!this.searchCity || has(b.city, this.searchCity)) &&
      (!this.searchCountry || has(b.country, this.searchCountry)));
  }

  applyClientSort(bars: any[]): void {
    const sorted = [...bars];
    switch (this.sortBy) {
      case 'rating':
        sorted.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
        break;
      case 'name':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'city':
        sorted.sort((a, b) => (a.city || '').localeCompare(b.city || ''));
        break;
    }
    this.filteredBars = sorted;
  }

  onSearch(): void {
    this.loadBars();
  }

  onSortChange(): void {
    if (this.sortBy === 'rating') {
      this.loadBars();
    } else {
      this.applyClientSort(this.bars);
    }
  }

  clearFilters(): void {
    this.searchName = '';
    this.searchCity = '';
    this.searchCountry = '';
    this.sortBy = 'name';
    // Back to the full radius, NOT to every bar on the platform: clearing the
    // filters should widen to the 100km limit and stop there.
    this.radiusKm = this.MAX_RADIUS_KM;
    this.loadBars();
  }

  /**
   * The explicit "Bars Near Me" control.
   *
   * It used to carry its own copy of the geolocation call, the fetch and the
   * result handling — so the radius cap, the text filters and the sort all had
   * to be remembered in two places, and the button's results ignored the
   * filters the customer had typed. It now just (re)locates and goes through
   * loadBars like everything else.
   */
  findNearbyBars(): void {
    if (!navigator.geolocation) {
      this.snackBar.open('Geolocation is not supported by your browser', 'Close', { duration: 4000 });
      return;
    }
    if (this.coords) { this.loadBars(); return; }
    this.locateThenLoad();
  }

  /** The radius control moved. Re-query — the cap is applied in loadBars. */
  onRadiusChange(): void {
    if (this.radiusKm > this.MAX_RADIUS_KM) this.radiusKm = this.MAX_RADIUS_KM;
    if (this.radiusKm < 1) this.radiusKm = 1;
    if (this.coords) this.loadBars();
  }

  selectBar(bar: any): void {
    this.selectedBar = bar;
    this.popularDrinks = [];
    this.loadingDrinks = true;
    this.analyticsService.getDrinkPopularity().pipe(
      catchError(() => of([]))
    ).subscribe(drinks => {
      this.popularDrinks = drinks.slice(0, 5);
      this.loadingDrinks = false;
    });
  }

  closeDetail(): void {
    this.selectedBar = null;
    this.popularDrinks = [];
  }

  openRatingDialog(bar: any): void {
    this.ratingBarId = bar.id;
    this.ratingValue = 5;
    this.showRatingDialog = true;
  }

  closeRatingDialog(): void {
    this.showRatingDialog = false;
    this.ratingBarId = '';
  }

  submitRating(): void {
    this.authService.rateBar(this.ratingBarId, this.ratingValue).subscribe({
      next: (updatedBar) => {
        const idx = this.bars.findIndex(b => b.id === this.ratingBarId);
        if (idx !== -1) {
          this.bars[idx].averageRating = updatedBar.averageRating;
          this.bars[idx].totalRatings = updatedBar.totalRatings;
        }
        if (this.selectedBar?.id === this.ratingBarId) {
          this.selectedBar.averageRating = updatedBar.averageRating;
          this.selectedBar.totalRatings = updatedBar.totalRatings;
        }
        this.snackBar.open('Rating submitted!', 'Close', { duration: 3000 });
        this.closeRatingDialog();
        if (this.sortBy === 'rating') this.applyClientSort(this.bars);
      },
      error: () => this.snackBar.open('Failed to submit rating', 'Close', { duration: 3000 })
    });
  }

  visitBar(bar: any): void {
    this.router.navigate(['/customer'], { queryParams: { barId: bar.id } });
  }

  getStarArray(rating: number): number[] {
    return [1, 2, 3, 4, 5];
  }

  isStarFilled(star: number, rating: number): boolean {
    return star <= Math.round(rating);
  }
}
